import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { api, unwrap } from "../api/client";
import { UpdatesSection } from "./UpdatesSection";
import type { components } from "../api/schema";

type Settings = components["schemas"]["InstallationSettings"];

const KEY = ["settings"] as const;

/** Whisper's codes for the languages most heard in the UK; others by code. */
const LANGUAGES: [string, string][] = [
  ["auto", "Detect for each stretch of speech"],
  ["en", "English"],
  ["cy", "Welsh"],
  ["gd", "Scottish Gaelic"],
  ["pl", "Polish"],
  ["ro", "Romanian"],
  ["ur", "Urdu"],
  ["pa", "Punjabi"],
  ["ar", "Arabic"],
  ["bn", "Bengali"],
  ["pt", "Portuguese"],
  ["es", "Spanish"],
  ["fr", "French"],
  ["lt", "Lithuanian"],
];

/** Settings for the installation; changes are audited. */
export function SettingsPage() {
  const qc = useQueryClient();
  const settings = useQuery({
    queryKey: KEY,
    queryFn: () => unwrap(api.GET("/api/settings")),
  });
  const save = useMutation({
    mutationFn: (body: Settings) => unwrap(api.PUT("/api/settings", { body })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      // The menu and pages follow simple mode.
      void qc.invalidateQueries({ queryKey: ["session"] });
    },
  });
  const s = settings.data;
  return (
    <Stack spacing={3} component="section" aria-label="Settings">
      <Typography variant="h5" component="h2">
        Settings
      </Typography>
      {settings.isError && (
        <Alert severity="error">{settings.error.message}</Alert>
      )}
      {save.isError && <Alert severity="error">{save.error.message}</Alert>}
      {s && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            {s.desktop && (
              <div>
                <FormControlLabel
                  control={
                    <Switch
                      checked={s.advanced}
                      disabled={save.isPending}
                      onChange={(e) =>
                        save.mutate({ ...s, advanced: e.target.checked })
                      }
                    />
                  }
                  label="Advanced: teams, every role and case grants"
                />
                <Typography variant="body2" color="text.secondary">
                  Off, AVGV keeps everything in one library: accounts are
                  Administrators or Users, everyone joins the library and every
                  case is open to everyone. Turn it on to organise people into
                  teams with their own libraries, choose roles in detail, and
                  open each case to chosen people only.
                </Typography>
              </div>
            )}
            <div>
              <FormControlLabel
                control={
                  <Switch
                    checked={s.export_self_approval}
                    disabled={save.isPending}
                    onChange={(e) =>
                      save.mutate({
                        ...s,
                        export_self_approval: e.target.checked,
                      })
                    }
                  />
                }
                label="People may approve their own exports"
              />
              <Typography variant="body2" color="text.secondary">
                With their password again, recorded as a self-approval in the
                audit log and in the package. Off, a second person with approval
                rights must approve each export.
              </Typography>
            </div>
            {!s.desktop && (
              <div>
                <TextField
                  type="number"
                  size="small"
                  label="Remember a browser for (days)"
                  defaultValue={s.mfa_remember_days}
                  slotProps={{ htmlInput: { min: 0, max: 90 } }}
                  onBlur={(e) => {
                    const days = Number(e.target.value);
                    if (days !== s.mfa_remember_days)
                      save.mutate({ ...s, mfa_remember_days: days });
                  }}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  After the two-factor code, people may tick &quot;Remember this
                  browser&quot; to skip the code there for this many days. 0
                  asks every time.
                </Typography>
              </div>
            )}
            <div>
              <TextField
                select
                size="small"
                label="Transcripts are in"
                value={s.transcript_language}
                disabled={save.isPending}
                onChange={(e) =>
                  save.mutate({ ...s, transcript_language: e.target.value })
                }
                sx={{ minWidth: 300 }}
              >
                {LANGUAGES.map(([code, name]) => (
                  <MenuItem key={code} value={code}>
                    {name}
                  </MenuItem>
                ))}
                {!LANGUAGES.some(([c]) => c === s.transcript_language) && (
                  <MenuItem value={s.transcript_language}>
                    {s.transcript_language}
                  </MenuItem>
                )}
              </TextField>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                What AVGV writes down of the speech in recordings. One language
                is more accurate than detecting it, which can mistake noise for
                another language. Speech in a language other than English is
                also translated into English. Recordings already transcribed
                keep their transcripts until analysis is run again.
              </Typography>
            </div>
          </Stack>
        </Paper>
      )}
      <UpdatesSection />
    </Stack>
  );
}
